import { BOUNDARIES } from "@/content/journey";
import { engageHref } from "@/content/engage";
import { PageIntro } from "@/components/site/PageIntro";
import { EngageBand } from "@/components/site/EngageBand";
import { ActionAnchor, ActionLink, Container, Eyebrow, Mono, SectionTitle, TextLink } from "@/components/site/primitives";
import { JourneyExplorer } from "@/components/explorers/JourneyExplorer";

const IntakePreview = () => (
  <div data-testid="diagnostic-intake-preview" className="border border-o-strong bg-o-surface">
    <div className="border-b border-dashed border-o-proof px-5 py-3">
      <span className="font-mono text-[10.5px] uppercase tracking-[0.2em] text-o-proof">Structural preview · not a working service</span>
    </div>
    <div className="p-5 sm:p-6">
      <label htmlFor="intake-preview" className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-o-dim">Your problem, in your own words</label>
      <textarea id="intake-preview" disabled rows={6} placeholder="Intake layout only. This field is inactive in Phase 1."
        className="field-input mt-2 cursor-not-allowed opacity-70" data-testid="diagnostic-intake-textarea" />
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <button type="button" disabled data-testid="diagnostic-intake-submit" className="cursor-not-allowed border border-o-line px-4 py-2.5 text-sm text-o-dim">
          Not yet operating
        </button>
        <TextLink to={engageHref("business", "/enterprise/diagnostic")} testid="diagnostic-intake-engage">Use the business inquiry preview</TextLink>
      </div>
    </div>
  </div>
);

const Boundaries = () => (
  <section className="border-b border-o-line" data-testid="diagnostic-boundaries">
    <Container className="py-16 lg:py-20">
      <Eyebrow index="01">What you can rely on</Eyebrow>
      <div className="mt-8 grid gap-px border border-o-line bg-o-line md:grid-cols-3">
        {BOUNDARIES.map((b, i) => (
          <div key={b.title} className="bg-o-surface p-6 sm:p-8">
            <Mono accent>0{i + 1}</Mono>
            <h3 className="mt-6 font-display text-xl font-bold">{b.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-o-dim">{b.text}</p>
          </div>
        ))}
      </div>
    </Container>
  </section>
);

const Connected = () => (
  <section className="border-b border-o-line" data-testid="diagnostic-connected">
    <Container className="grid gap-4 py-16 md:grid-cols-2 lg:py-20">
      <div className="border border-o-line bg-o-surface p-6 sm:p-8">
        <Mono accent>What may be built</Mono>
        <p className="mt-3 leading-relaxed">If a diagnosis warrants software, it may draw on one or more of the four applied capabilities, fitted to your situation.</p>
        <TextLink to="/enterprise/software" testid="diagnostic-software-link" className="mt-5">The applied software family</TextLink>
      </div>
      <div className="border border-o-line p-6 sm:p-8">
        <Mono accent>The wider operation</Mono>
        <p className="mt-3 leading-relaxed">Ordo Diagnostic is the customer entrance to Diagnostics & Software, which also delivers architectural advisory, implementation, and continuing support.</p>
        <TextLink to="/enterprise" testid="diagnostic-enterprise-link" className="mt-5">Diagnostics & Software</TextLink>
      </div>
    </Container>
  </section>
);

export default function DiagnosticPage() {
  return (
    <>
      <PageIntro routeKey="diagnostic" group="enterprise" index="OD" eyebrow="Ordo Diagnostic · The customer entrance"
        title="Tell us one problem your business keeps dealing with."
        lede="Ordo Diagnostic begins with your actual organizational situation. It investigates what needs to change and determines an appropriate response before anything is built."
        aside={<IntakePreview />}>
        <div className="mt-10 flex flex-wrap gap-3">
          <ActionAnchor href="#journey" testid="diagnostic-journey-btn">See how the relationship works</ActionAnchor>
          <ActionLink variant="line" to={engageHref("business", "/enterprise/diagnostic")} testid="diagnostic-engage-btn">Start a business inquiry</ActionLink>
        </div>
      </PageIntro>
      <Boundaries />
      <section id="journey" className="border-b border-o-line" data-testid="diagnostic-journey">
        <Container className="py-16 lg:py-24">
          <div className="mb-10 grid gap-6 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-7">
              <Eyebrow index="02">The customer relationship</Eyebrow>
              <SectionTitle className="mt-5 text-2xl sm:text-3xl lg:text-4xl">Understand → Diagnose → Build → Connect → Improve</SectionTitle>
            </div>
            <p className="text-o-dim lg:col-span-4 lg:col-start-9">Each stage shows what you experience, what Ordo Intelligence carries, what you receive, and whether a new decision is involved.</p>
          </div>
          <JourneyExplorer />
        </Container>
      </section>
      <Connected />
      <EngageBand path="business" from="/enterprise/diagnostic" title="Bring one problem."
        text="Start a business inquiry. During Phase 1 the inquiry form is an inactive preview." />
    </>
  );
}
